import type {
  CreateUsageRequest,
  UpdateUsageRequest,
  Usage,
} from "~/api/fga-types";

/**
 * Staged-change model and commit planner for the Blueprint Builder's build
 * phase. The builder stages adds, edits and deletes locally, referencing
 * not-yet-created nodes by client-side temp ids (`staged-N`). This module
 * reconciles those changes into server-callable steps, orders them so parents
 * are created before children, and tracks which steps landed, so a commit
 * that fails part way can be retried without re-sending steps that already
 * succeeded.
 */

/** A resource added in the build phase, not yet created on the server. */
export interface StagedAdd {
  /** Client-side temp id (`staged-N`), mapped to a server id at commit. */
  tempId: string;
  platformSlug: string;
  /** Server usage id, the temp id of another staged add, or null for roots. */
  parentUsageId: string | null;
  typeId: string;
  qualifiedName: string;
}

/**
 * A qualified-name change. `usageId` is a server usage id, or the temp id of
 * a staged add (folded into that add at commit, never PATCHed on its own).
 */
export interface StagedEdit {
  usageId: string;
  qualifiedName: string;
}

export interface StagedChanges {
  adds: StagedAdd[];
  edits: StagedEdit[];
  /** Server usage ids staged for deletion. Never temp ids. */
  deletes: string[];
}

export const EMPTY_STAGED: StagedChanges = {
  adds: [],
  edits: [],
  deletes: [],
};

let tempIdCounter = 0;

export function nextTempId(): string {
  return `staged-${++tempIdCounter}`;
}

export function hasStagedChanges(changes: StagedChanges): boolean {
  return (
    changes.adds.length > 0 ||
    changes.edits.length > 0 ||
    changes.deletes.length > 0
  );
}

/**
 * Stages the deletion of a node. A staged node has no server id, so it is
 * removed from the pending adds together with its staged descendants, which
 * would otherwise be committed under a parent id that no longer exists. A
 * server node records the delete and also drops its staged descendants, for
 * the same reason: the deletes run before the adds.
 */
export function stageDelete(
  changes: StagedChanges,
  usageId: string,
): StagedChanges {
  const isStagedNode = changes.adds.some((a) => a.tempId === usageId);
  const doomed = stagedSubtreeIds(changes.adds, usageId);
  return {
    adds: changes.adds.filter((a) => !doomed.has(a.tempId)),
    edits: changes.edits.filter((e) => !doomed.has(e.usageId)),
    deletes: isStagedNode
      ? changes.deletes
      : changes.deletes.includes(usageId)
        ? changes.deletes
        : [...changes.deletes, usageId],
  };
}

/**
 * The temp ids of staged adds that must go when `rootId` is deleted: the
 * staged node itself when `rootId` is a staged add, plus every staged add
 * below the deleted node (staged or server). A server `rootId` is not
 * included, so an edit keyed on it survives until reconcile drops it.
 */
function stagedSubtreeIds(adds: StagedAdd[], rootId: string): Set<string> {
  const doomed = new Set<string>();
  if (adds.some((a) => a.tempId === rootId)) {
    doomed.add(rootId);
  }
  let grew = true;
  while (grew) {
    grew = false;
    for (const add of adds) {
      if (doomed.has(add.tempId)) continue;
      if (
        add.parentUsageId !== null &&
        (add.parentUsageId === rootId || doomed.has(add.parentUsageId))
      ) {
        doomed.add(add.tempId);
        grew = true;
      }
    }
  }
  return doomed;
}

export type CommitStep =
  | { kind: "delete"; usageId: string }
  | { kind: "edit"; usageId: string; qualifiedName: string }
  | ({ kind: "add" } & StagedAdd);

export interface CommitPlan {
  /** The reconciled staged state the steps were planned from. */
  staged: StagedChanges;
  steps: CommitStep[];
}

/**
 * Drops staged edits that cannot be applied: edits for ids staged for delete,
 * and edits keyed by a staged node's temp id, which fold into that node's add
 * payload instead of becoming a PATCH against a temp id.
 */
function reconcile(changes: StagedChanges): StagedChanges {
  const deleted = new Set(changes.deletes);
  const addTempIds = new Set(changes.adds.map((a) => a.tempId));
  const foldedNames = new Map<string, string>();
  const edits: StagedEdit[] = [];
  for (const edit of changes.edits) {
    if (deleted.has(edit.usageId)) continue;
    if (addTempIds.has(edit.usageId)) {
      foldedNames.set(edit.usageId, edit.qualifiedName);
      continue;
    }
    edits.push(edit);
  }
  const adds =
    foldedNames.size > 0
      ? changes.adds.map((a) => {
          const folded = foldedNames.get(a.tempId);
          return folded === undefined ? a : { ...a, qualifiedName: folded };
        })
      : changes.adds;
  return { adds, edits, deletes: changes.deletes };
}

/**
 * Orders staged adds parents before children, so a parent's server id is
 * known by the time its children are created.
 */
function orderAdds(adds: StagedAdd[]): StagedAdd[] {
  const byTempId = new Map(adds.map((a) => [a.tempId, a]));
  const emitted = new Set<string>();
  const ordered: StagedAdd[] = [];

  const emit = (add: StagedAdd, path: Set<string>) => {
    if (emitted.has(add.tempId) || path.has(add.tempId)) return;
    path.add(add.tempId);
    const parent =
      add.parentUsageId === null ? undefined : byTempId.get(add.parentUsageId);
    if (parent) emit(parent, path);
    path.delete(add.tempId);
    emitted.add(add.tempId);
    ordered.push(add);
  };

  for (const add of adds) emit(add, new Set());
  return ordered;
}

/** Plans the commit: deletes first, then edits, then adds in dependency order. */
export function prepareCommit(changes: StagedChanges): CommitPlan {
  const staged = reconcile(changes);
  return {
    staged,
    steps: [
      ...staged.deletes.map(
        (usageId): CommitStep => ({ kind: "delete", usageId }),
      ),
      ...staged.edits.map(
        (edit): CommitStep => ({
          kind: "edit",
          usageId: edit.usageId,
          qualifiedName: edit.qualifiedName,
        }),
      ),
      ...orderAdds(staged.adds).map(
        (add): CommitStep => ({
          kind: "add",
          ...add,
        }),
      ),
    ],
  };
}

/**
 * Maps a parent reference to its server id once the add landed. Temp ids of
 * adds that have not landed yet pass through unchanged; parents always land
 * before their children, so a mapped parent is always available when needed.
 */
export function resolveParentUsageId(
  parentUsageId: string,
  idMap: ReadonlyMap<string, string>,
): string {
  return idMap.get(parentUsageId) ?? parentUsageId;
}

/**
 * Removes a landed step from the staged state. When an add lands, its temp id
 * is rewritten to the server id for the remaining staged adds, so a retry
 * sends only the steps that did not land, with ids the server can resolve.
 */
export function applyLandedStep(
  staged: StagedChanges,
  step: CommitStep,
  idMap: ReadonlyMap<string, string>,
): StagedChanges {
  switch (step.kind) {
    case "delete":
      return {
        ...staged,
        deletes: staged.deletes.filter((d) => d !== step.usageId),
      };
    case "edit":
      return {
        ...staged,
        edits: staged.edits.filter((e) => e.usageId !== step.usageId),
      };
    case "add": {
      const serverId = idMap.get(step.tempId);
      if (serverId === undefined) return staged;
      return {
        ...staged,
        adds: staged.adds
          .filter((a) => a.tempId !== step.tempId)
          .map((a) =>
            a.parentUsageId === step.tempId
              ? { ...a, parentUsageId: serverId }
              : a,
          ),
      };
    }
  }
}

/**
 * Human-readable label for a step, used in commit error messages so they
 * name the node that failed instead of a URL. `deletedNodeLabel` resolves a
 * server usage id to its qualified name; without it the raw id is shown.
 */
export function stepLabel(
  step: CommitStep,
  deletedNodeLabel: (usageId: string) => string = (usageId) => usageId,
): string {
  switch (step.kind) {
    case "delete":
      return `delete "${deletedNodeLabel(step.usageId)}"`;
    case "edit":
      return `update "${step.qualifiedName}"`;
    case "add":
      return `add "${step.qualifiedName}"`;
  }
}

/** The catalog calls the commit needs, injected so tests can fake them. */
export interface CommitApi {
  createUsage: (
    platformSlug: string,
    req: CreateUsageRequest,
  ) => Promise<Usage>;
  updateUsage: (usageId: string, req: UpdateUsageRequest) => Promise<Usage>;
  deleteUsage: (usageId: string) => Promise<void>;
}

export interface CommitOutcome {
  /**
   * The staged changes that did not land. Equal to `EMPTY_STAGED` when every
   * step succeeded.
   */
  staged: StagedChanges;
  /** The step that failed, or null when the commit completed. */
  failedStep: CommitStep | null;
  /** The error thrown by the failed step, if any. */
  error: unknown;
}

/**
 * Runs the planned steps one by one. Each step that lands is removed from the
 * staged state, so when a step fails, the returned staged state holds only
 * the work that is left and a retry does not re-send steps that already
 * landed.
 */
export async function runCommit(
  changes: StagedChanges,
  api: CommitApi,
  onLanded?: (staged: StagedChanges) => void,
): Promise<CommitOutcome> {
  const plan = prepareCommit(changes);
  let staged = plan.staged;
  const idMap = new Map<string, string>();
  for (const step of plan.steps) {
    try {
      if (step.kind === "delete") {
        await api.deleteUsage(step.usageId);
      } else if (step.kind === "edit") {
        await api.updateUsage(step.usageId, {
          qualifiedName: step.qualifiedName,
        });
      } else {
        const usage = await api.createUsage(step.platformSlug, {
          typeId: step.typeId,
          parentUsageId:
            step.parentUsageId === null
              ? undefined
              : resolveParentUsageId(step.parentUsageId, idMap),
          qualifiedName: step.qualifiedName,
        });
        idMap.set(step.tempId, usage.id);
      }
    } catch (error) {
      return { staged, failedStep: step, error };
    }
    staged = applyLandedStep(staged, step, idMap);
    onLanded?.(staged);
  }
  return { staged, failedStep: null, error: null };
}
